"use client";
import { useEffect, useRef } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal-dialog behaviour for a plain `<div role="dialog" aria-modal="true">`:
 * move focus in when it opens, keep Tab inside it, close on Escape, and hand
 * focus back to whatever opened it. Spread the returned props onto the dialog
 * element; render the dialog conditionally, since mount/unmount IS open/close.
 *
 * Escape is a React handler, not a document listener, on purpose: a nested
 * control (SelectMenu) can then swallow its own Escape with stopPropagation.
 * A native listener on this node would already have fired by the time React's
 * root-level handler ran, so stopPropagation could not reach it.
 */
export function useDialog<T extends HTMLElement>(onClose: () => void) {
  const ref = useRef<T>(null);
  // Held in a ref so the focus effect can depend on nothing: callers pass an
  // inline arrow, and re-running on every render would steal focus back to the
  // first control each time the dialog's contents change (lightbox next/prev).
  const closeRef = useRef(onClose);
  // Synced in an effect, not during render: the handler below is the only
  // reader and it can't fire before the commit that runs this.
  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const node = ref.current;
    // Prefer the first real control; fall back to the container, which carries
    // tabIndex={-1}, so focus is never left outside the dialog.
    (node?.querySelector<HTMLElement>(FOCUSABLE) ?? node)?.focus();
    return () => {
      if (opener && typeof opener.focus === "function" && document.contains(opener)) opener.focus();
    };
  }, []);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      closeRef.current();
      return;
    }
    if (e.key !== "Tab") return;
    const node = ref.current;
    if (!node) return;
    const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === node)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return { ref, tabIndex: -1, onKeyDown } as const;
}
